import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

import { rendererFor } from '../blocks/registry'
import type { Block, DeckDefinition, Slide, Story, Transition } from '../types/presentation.types'
import type { BlockView } from './useResolvedBlocks'

/**
 * Where the presenter is, and how it moves (MYD-496).
 *
 * The index is 0-based internally and 1-based in the URL (`?slide=3`), which
 * is what a person reads on screen and would type into a link. The URL is
 * kept in step with `replace`, so a reload resumes on the same slide without
 * every slide becoming a Back-button entry.
 *
 * Keyboard handling is a pure function of a key and the state, returned as
 * `handleKey`, so the screen owns the listener and the rules stay testable.
 */

export interface PresentedSlide {
	slide: Slide
	story: Story
	storyIndex: number
	/**
	 * A generated story divider (MYD-497), not a slide in the deck. Its `slide`
	 * is a stand-in with no blocks, so everything that walks slides (numbering,
	 * transitions, the overview) treats it like any other.
	 */
	divider?: boolean
	/**
	 * A generated continuation of the slide before it (MYD-504): the next page
	 * of a block that runs past one slide, such as an Explorer table. `page`
	 * is 0-based, so the first continuation is page 1.
	 */
	continuation?: { page: number }
}

/** How many pages a resolved block fills, per its renderer. */
const pagesOf = (block: Block, view: BlockView | undefined): number => {
	const pages = view?.status === 'ok' ? rendererFor(block.element)?.pages?.(view.payload) : undefined
	return typeof pages === 'number' && pages > 1 ? Math.floor(pages) : 1
}

/**
 * The continuation slides a slide needs once its blocks have resolved: one per
 * page past the first, each carrying only the blocks that still have a page
 * to show, full width, under the slide's own title.
 *
 * Their ids derive from the slide's, so the presenter can keep its place when
 * they appear (they arrive with the story's data, after the deck itself).
 */
const continuationsOf = (slide: Slide, blocks: Record<string, BlockView | undefined>): Slide[] => {
	const paged = slide.blocks.map(block => ({ block, pages: pagesOf(block, blocks[block.id]) })).filter(entry => entry.pages > 1)
	const most = Math.max(1, ...paged.map(entry => entry.pages))
	return Array.from({ length: most - 1 }, (_, index) => ({
		id: `${slide.id}~${index + 2}`,
		layout: 'title-body',
		title: slide.title ? `${slide.title} (continued)` : '',
		subtitle: '',
		speakerNotes: '',
		transition: slide.transition,
		blocks: paged.filter(entry => entry.pages > index + 1).map(entry => ({ ...entry.block, region: 'main' })),
	}))
}

/** The stand-in slide for a story's divider. Its id cannot collide with a real one. */
const dividerSlide = (story: Story): Slide => ({
	id: `divider:${story.id}`,
	layout: 'section',
	title: story.title,
	subtitle: '',
	speakerNotes: '',
	transition: null,
	blocks: [],
})

/**
 * Every slide of the deck in presentation order, with its story. A story that
 * asks for a divider, and has slides to introduce, gets one first. A slide
 * whose resolved content runs long is followed by its continuations, so the
 * list grows as stories resolve.
 */
export const presentationSlides = (deck: DeckDefinition, blocks: Record<string, BlockView | undefined> = {}): PresentedSlide[] =>
	deck.stories.flatMap((story, storyIndex) => [
		...(story.divider && story.slides.length > 0 ? [{ slide: dividerSlide(story), story, storyIndex, divider: true }] : []),
		...story.slides.flatMap(slide => [
			{ slide, story, storyIndex },
			...continuationsOf(slide, blocks).map((continued, index) => ({ slide: continued, story, storyIndex, continuation: { page: index + 1 } })),
		]),
	])

/**
 * Where the slide now at `key` sits in a new list, for keeping the presenter's
 * place when continuation slides appear before it. Null when it is gone.
 */
export const indexOfSlide = (slides: PresentedSlide[], key: string | null): number | null => {
	if (!key) return null
	const index = slides.findIndex(entry => entry.slide.id === key)
	return index >= 0 ? index : null
}

export interface StorySpan {
	storyIndex: number
	title: string
	/** Index of the story's first presented slide (its divider, if it has one). */
	start: number
	count: number
}

/** Where each story begins and how long it runs, for the progress bar and the story menu. */
export const storySpans = (slides: PresentedSlide[]): StorySpan[] => {
	const spans: StorySpan[] = []
	slides.forEach((entry, index) => {
		const last = spans[spans.length - 1]
		if (last && last.storyIndex === entry.storyIndex) last.count += 1
		else spans.push({ storyIndex: entry.storyIndex, title: entry.story.title || `Story ${entry.storyIndex + 1}`, start: index, count: 1 })
	})
	return spans
}

/**
 * The presented index of the first slide of the first story about an asset,
 * or null. How "Present" from an asset's page lands on that asset's story.
 */
export const startIndexForAnchor = (slides: PresentedSlide[], anchor: string | null): number | null => {
	if (!anchor) return null
	const [scope, entityId] = anchor.split(':')
	const index = slides.findIndex(entry => entry.story.anchor?.scope === scope && entry.story.anchor?.entityId === entityId)
	return index >= 0 ? index : null
}

export type MoveKind = 'next' | 'previous' | 'jump'

export interface Move {
	from: number
	to: number
	kind: MoveKind
	/** Bumped per move, so two moves to the same slide are still two moves. */
	serial: number
}

/**
 * The transition a move plays. Forward uses the transition INTO the new
 * slide; backward plays the transition into the slide being left, in reverse,
 * so going back undoes going forward. A jump (overview, Home/End, a typed
 * number) crossfades: a page turn across twelve slides would be a lie.
 */
export const transitionFor = (move: Move | null, slides: PresentedSlide[], deckTransition: Transition): Transition => {
	if (!move || move.kind === 'jump') return move ? 'fade' : 'none'
	const slide = move.kind === 'next' ? slides[move.to]?.slide : slides[move.from]?.slide
	return slide?.transition ?? deckTransition
}

/** How long a typed slide number waits for Enter before it is forgotten. */
const NUMBER_TIMEOUT_MS = 3000

export interface UsePresenter {
	index: number
	total: number
	move: Move | null
	overview: boolean
	/** The story menu (S): jump to any story's first slide (MYD-497). */
	storyMenu: boolean
	/** The in-screen speaker notes (N), for presenting on one screen (MYD-497). */
	notes: boolean
	/** Digits typed so far towards a "number + Enter" jump. */
	typed: string
	go: (to: number, kind?: MoveKind) => void
	/**
	 * Follow the slide on screen to its new index after slides were inserted
	 * before it (MYD-504). Not a move: no transition plays and nothing is
	 * announced as a step.
	 */
	relocate: (to: number) => void
	next: () => void
	previous: () => void
	setOverview: (open: boolean) => void
	setStoryMenu: (open: boolean) => void
	setNotes: (open: boolean) => void
	/**
	 * What a key did. `presenter-view` asks the screen to open the second
	 * window (P), which only the screen can do.
	 */
	handleKey: (key: string) => 'handled' | 'exit' | 'presenter-view' | 'ignored'
}

const clamp = (value: number, total: number) => Math.max(0, Math.min(value, Math.max(0, total - 1)))

/**
 * @param total   presented slides, dividers included
 * @param startAt where to begin when the URL has no ?slide (MYD-497: "Present"
 *                from an asset's page starts at that asset's story)
 */
export function usePresenter(total: number, startAt: number | null = null): UsePresenter {
	const [params, setParams] = useSearchParams()
	const locationState = useLocation().state
	const initial = () => (params.get('slide') ? Number(params.get('slide')) - 1 || 0 : (startAt ?? 0))
	const [index, setIndex] = useState(() => clamp(initial(), total))
	const [move, setMove] = useState<Move | null>(null)
	const [overview, setOverview] = useState(false)
	const [storyMenu, setStoryMenu] = useState(false)
	const [notes, setNotes] = useState(false)
	const [typed, setTyped] = useState('')
	const serial = useRef(0)
	const indexRef = useRef(index)
	indexRef.current = index

	// The deck loads after the first render, so `total` starts at 0 and the
	// URL's slide number could not be honoured then. Re-read it once the real
	// total is known.
	const settled = useRef(false)
	useEffect(() => {
		if (settled.current || total === 0) return
		settled.current = true
		setIndex(clamp(initial(), total))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [total, params, startAt])

	// Keep ?slide= in step, without adding history entries. The router state
	// is carried across: a replace would otherwise drop it, and it is where
	// the presenter learns what to return to on exit.
	useEffect(() => {
		if (total === 0) return
		const wanted = String(index + 1)
		if (params.get('slide') === wanted) return
		const next = new URLSearchParams(params)
		next.set('slide', wanted)
		setParams(next, { replace: true, state: locationState })
	}, [index, total, params, setParams, locationState])

	// A typed number is forgotten after a pause, so a stray digit does not
	// hijack the next Enter minutes later.
	useEffect(() => {
		if (!typed) return
		const timer = setTimeout(() => setTyped(''), NUMBER_TIMEOUT_MS)
		return () => clearTimeout(timer)
	}, [typed])

	const go = useCallback(
		(to: number, kind: MoveKind = 'jump') => {
			const from = indexRef.current
			const target = clamp(to, total)
			if (target === from) return
			serial.current += 1
			setMove({ from, to: target, kind, serial: serial.current })
			setIndex(target)
		},
		[total],
	)

	const relocate = useCallback(
		(to: number) => {
			const target = clamp(to, total)
			if (target === indexRef.current) return
			setMove(null)
			setIndex(target)
		},
		[total],
	)

	const next = useCallback(() => go(indexRef.current + 1, 'next'), [go])
	const previous = useCallback(() => go(indexRef.current - 1, 'previous'), [go])

	const handleKey = useCallback(
		(key: string): 'handled' | 'exit' | 'presenter-view' | 'ignored' => {
			if (/^[0-9]$/.test(key)) {
				setTyped(current => (current + key).slice(-4))
				return 'handled'
			}
			if (key === 'Enter' && typed) {
				const number = Number(typed)
				setTyped('')
				if (number >= 1) go(number - 1, 'jump')
				setOverview(false)
				setStoryMenu(false)
				return 'handled'
			}
			if (key === 'Escape') {
				// Esc backs out one layer at a time: a half-typed number, then an
				// open panel, then the notes, then the presentation itself.
				if (typed) {
					setTyped('')
					return 'handled'
				}
				if (overview || storyMenu) {
					setOverview(false)
					setStoryMenu(false)
					return 'handled'
				}
				if (notes) {
					setNotes(false)
					return 'handled'
				}
				return 'exit'
			}
			// The two panels are exclusive: opening one closes the other.
			if (key === 'g' || key === 'G') {
				setOverview(open => !open)
				setStoryMenu(false)
				return 'handled'
			}
			if (key === 's' || key === 'S') {
				setStoryMenu(open => !open)
				setOverview(false)
				return 'handled'
			}
			if (key === 'n' || key === 'N') {
				setNotes(open => !open)
				return 'handled'
			}
			if (key === 'p' || key === 'P') return 'presenter-view'
			if (overview || storyMenu) return 'ignored'

			switch (key) {
				case 'ArrowRight':
				case 'ArrowDown':
				case 'PageDown':
				case ' ':
				case 'Enter':
					next()
					return 'handled'
				case 'ArrowLeft':
				case 'ArrowUp':
				case 'PageUp':
				case 'Backspace':
					previous()
					return 'handled'
				case 'Home':
					go(0, 'jump')
					return 'handled'
				case 'End':
					go(total - 1, 'jump')
					return 'handled'
				default:
					return 'ignored'
			}
		},
		[typed, overview, storyMenu, notes, go, next, previous, total],
	)

	return useMemo(
		() => ({ index, total, move, overview, storyMenu, notes, typed, go, relocate, next, previous, setOverview, setStoryMenu, setNotes, handleKey }),
		[index, total, move, overview, storyMenu, notes, typed, go, relocate, next, previous, handleKey],
	)
}

/**
 * Keep the slide on screen on screen when the list changes under it (MYD-504).
 *
 * Continuation slides arrive with a story's data, after the deck. A story
 * resolving BEFORE the current slide (a jump straight into a later story,
 * then back) inserts slides ahead of it, and an index alone would then point
 * at a different slide. The slide's id does not move, so follow that.
 */
export function useKeepSlideInPlace(slides: PresentedSlide[], presenter: Pick<UsePresenter, 'index' | 'relocate'>) {
	const onScreen = useRef<string | null>(null)
	const { index, relocate } = presenter

	// Declared first, so on the render where the list changes it reads the id
	// from BEFORE the change.
	useEffect(() => {
		const found = indexOfSlide(slides, onScreen.current)
		if (found !== null && found !== index) relocate(found)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [slides])

	useEffect(() => {
		onScreen.current = slides[index]?.slide.id ?? null
	}, [slides, index])
}

export default usePresenter
